import { Box, Grid } from "@dextech-ai/dex-design-system"
import { ReactComponent as IconEye } from "@/assets/svgs/Union-eye.svg"
import { ReactComponent as IconPerson } from '@/assets/svgs/icon-persona.svg'
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg'
import { ReactComponent as UserGroup } from "@/assets/svgs/user-group.svg"
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import InputSuffixIcon from '@/components/InputSuffixIcon'
import Portal from '@/components/Portal'
import SelectorBox from '@/components/SelectorBox2/index'
import StepList from '@/components/StepList/index'
import Toast from '@/components/Toast'
import ToggleSwitch3 from '@/components/ToggleSwitch3/index'
import Tooltip from '@/components/Tooltip/Tooltip'
import useBlockchainNetworks from "@/hooks/use-blockchain-networks/index"
import useModal from '@/hooks/use-modal'
import usePage from '@/hooks/use-page'
import usePersona from '@/hooks/use-personas'
import useStepList from '@/hooks/use-step-list'
import useToast from '@/hooks/use-toast'
import useUseCases from '@/hooks/use-use-cases'
import { isAgentPrincipalEnabled, isAgentPrincipalPendingApproval } from '@/hooks/use-use-cases/index'
import { useLocation, useNavigate, useParams } from '@/lib/router-dom'
import api from '@/services/api/orgPersonas.js'
import rem from '@/utils/rem'
import { getUseCaseId } from "@/utils/use-case-storage"
import produce from 'immer'
import { isEqual } from "lodash"
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import AgentPersonaModal from "./components/AgentModal/index"
import AgentPrincipalInformationModal from './components/AgentPrincipalInformationModal/index'
import AgentPrincipalTnCModal from './components/AgentPrincipalTnCModal/index'
import DataElementCard from "./components/ConfirmationList/DataElementCard"
import DisableEtrIssuance from "./components/DisableEtrIssuanceModal/index"
import ExcludeAllEtrIssuance from "./components/ExcludeAllEtrIssuanceModal/index"
import PersonaInfoModal from "./components/PersonaInfoModal/index"
import ConfigureIssuanceSection from "./etr-issuance/configure-issuance"
import { ConfigureIssuanceCard } from "./etr-issuance/configure-issuance-card"
import { feature } from "@/utils/feature-verifier"
import { featureConstant } from "@/constants/configuration/feature"
import Search from '@/componentsDS/Search/index';
import Stepper from "@/componentsDS/Stepper/Stepper"


const Page = ({ role }) => {
	/*=================================================
		General
	/================================================*/

	const { organizationId, useCaseId } = useParams()
	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);

	const [pageTitle, setPageTitle] = useState('Edit Persona')
	const [subPageTitle, setSubPageTitle] = useState(useCase.name)

	const { breadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: [subPageTitle, pageTitle] })

	const { goTo } = useNavigate()

	const user = useSelector(state => state.auth.user)

	const toastType = {
		success: "success",
		error: "error"
	}

	const statuses = {
		approved: "APPROVED",
		rejected: "REJECTED",
		revoked: "REVOKED",
		pending: "PENDING"
	}

	const [searchTerm, setSearchTerm] = useState("")
	const [noOfPersona, setNoOfPersona] = useState("0")
	const [orgId, setOrgId] = useState("")

	const [removePersonas, setRemovePersonas] = useState([]);
	const [addPersonas, setAddPersonas] = useState([])

	const [etrIssuanceEnabled, setEtrIssuanceEnabled] = useState(false);
	const toggleEtrIssuance = () => setEtrIssuanceEnabled(!etrIssuanceEnabled);

	// const location = history.location
	const { currentState } = useLocation()
	const participantData = currentState

	const [loading, setLoading] = useState(false)

	/*===============================================================
		Transactions on behalf of external participants - T&C Modal
	/================================================================*/
	const { visible: visibleParticipantsTncModal, show: showParticipantsTncModal, hide: hideParticipantsTncModal } = useModal()

	const [enableExternalParticipant, setEnableExternalParticipant] = useState(false)
	const [agentToggleHistory, setAgentToggleHistory] = useState(false);

	function handledEnableExternalParticipant(checked) {
		setAgentToggleHistory(checked)
		if (checked) {
			showParticipantsTncModal()
		} else {
			setEnableExternalParticipant(false)
		}
	}

	async function acceptParticipantTnc() {
		setEnableExternalParticipant(true)
		hideParticipantsTncModal();
	}

	/*================================================================
		Transactions on behalf of external participants - Info Modal
	/==================================================================*/
	const { visible: visibleParticipantInfoModal, show: showParticipantInfoModal, hide: hideParticipantInfoModal } = useModal()

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { find: findPersonas, findAndSetCurrentPersona, currentItem: persona, items: personaItems, setItems: setPersonaItems, error: errorPersona } = usePersona()
	const { find: findEnrolledPersonas, error: errorEnrolledPersona } = usePersona()
	const { find: findUseCase, currentItem: useCaseItem, setCurrentItem: setUseCaseItem, error: errorUseCase } = useUseCases()
	const {
		blockchainNetworks,
		loading: loadingBlockchainNetworks,
		error: errorBlockchainNetworks,
		toggleBlockchainNetwork,
		toggleAllBlockchainNetworks,
		fetchBlockchainNetworks,
		resetBlockchainNetworks,
	} = useBlockchainNetworks(useCase?.etrIssuance?.networkIDs ?? useCaseItem?.[0]?.etrIssuance?.networkIDs ?? []);

	const isUseCaseEtrEnabled = useMemo(() => {
		return useCase?.enableETR ?? useCaseItem?.[0]?.enableETR
	}, [useCase, useCaseItem])
	const isOrgEtrEnabled = useMemo(() => {
		return useCase?.etrIssuance?.isEnabled ?? useCaseItem?.[0]?.etrIssuance?.isEnabled ?? false
	}, [useCase, useCaseItem])
	const IsEtrPersonaArray = useMemo(() => {
		resetBlockchainNetworks(blockchainNetworks);
		return personaItems.some(persona => persona.$selected && persona.is_etr_issuer)
	}, [personaItems]);
	const existingNetworkIds = useMemo(() => useCase?.etrIssuance?.networkIDs ?? useCaseItem?.[0]?.etrIssuance?.networkIDs ?? [], [useCase, useCaseItem]);
	const selectedNetworkIds = useMemo(() => blockchainNetworks.filter(network => network.$selected).map(network => network.networkId), [blockchainNetworks]);
	const [isAnyBlockchainSelected, setIsAnyBlockchainSelected] = useState(false)
	useEffect(() => {
		if (blockchainNetworks) {
			const check = blockchainNetworks?.some((blockchain) => blockchain.$selected)
			setIsAnyBlockchainSelected(check)
		}
	}, [blockchainNetworks, isAnyBlockchainSelected])

	const isModified =
		personaItems?.filter(item => item.organizationPersonaId !== undefined && item.$selected === false)?.length > 0 ||
		personaItems?.filter(item => item.organizationPersonaId === undefined && item.$selected === true)?.length > 0;

	const getUseCase = {
		useCaseId: useCaseId,
		organizationId: orgId
	}

	const enrollPersona = {
		useCaseId: useCaseId,
		orgId: orgId
	}
	useEffect(() => {
		if (role === 'admin') {
			setOrgId(organizationId)
		}
		if (role === 'participant') {
			setOrgId(user.organizationId)
		}
	}, [])

	useEffect(() => {
		if (isUseCaseEtrEnabled) {
			fetchBlockchainNetworks();
		}
	}, [isUseCaseEtrEnabled])

	useEffect(() => {
		async function getData() {
			const personasTemp = await findPersonas(getUseCase)
			const enrolledPersonasTemp = await findEnrolledPersonas(enrollPersona)

			const updatedItem = personasTemp?.reduce((array, persona) => {
				const tempArray = enrolledPersonasTemp.filter(persona => persona.status === statuses.approved || persona.status === statuses.pending).filter(({ id }) => id === persona.id).map(enrolledPersona => { return { ...enrolledPersona, $selected: true } });
				if (tempArray.length) {
					array.push(...tempArray);
				} else array.push(persona);

				return array;
			}, []);

			setPersonaItems(updatedItem)
		}

		async function getUseCaseData() {

			const selectedUseCaseItem = await findUseCase(getUseCase)
			setUseCaseItem(selectedUseCaseItem)

		}

		if (orgId !== "") {
			getData()
			getUseCaseData()
		}
	}, [orgId])

	useEffect(() => {
		setEnableExternalParticipant(isAgentPrincipal_Enabled)
		setAgentToggleHistory(isAgentPrincipal_Enabled);
		setEtrIssuanceEnabled(useCase?.etrIssuance?.isEnabled ?? useCaseItem?.[0]?.etrIssuance?.isEnabled ?? false)
	}, [useCase, useCaseItem])

	useEffect(() => {
		addToastMessage(errorPersona, toastType.error)
	}, [errorPersona])

	useEffect(() => {
		addToastMessage(errorEnrolledPersona, toastType.error)
	}, [errorEnrolledPersona])

	useEffect(() => {
		addToastMessage(errorUseCase, toastType.error)
	}, [errorUseCase])

	useEffect(() => {
		if (noOfPersona === 0 && !isAgentPrincipal_PendingApproval) {
			setEnableExternalParticipant(false);
		}
	}, [noOfPersona]);

	/*=================================================
		Persona List
	/================================================*/

	const handleUpdatePersonaSelect = (item) => {
		const index = personaItems?.findIndex(persona => persona.id === item.id);
		const updatedItems = produce(personaItems, draft => {
			draft[index].$selected = !draft[index].$selected
		})
		setPersonaItems(updatedItems)
		if (noOfPersona === 0) {
			setEnableExternalParticipant(agentToggleHistory);
		}
	}

	async function handleCheckInfo(item) {
		await findAndSetCurrentPersona({ personaId: item.id, useCaseId: useCaseId })
		showPersonaInfo()
	}

	function clearPersonaSelection() {
		const updatedItems = produce(personaItems, draft => {
			draft.map((persona) => {
				if (persona.organizationPersonaId === undefined) {
					return persona.$selected = false
				}
				return persona.$selected = true
			})
		})

		setPersonaItems(updatedItems);
		setEnableExternalParticipant(isAgentPrincipal_Enabled);
	}

	useEffect(() => {
		const selectedPersona = personaItems?.filter((persona) => persona.$selected === true).length
		setNoOfPersona(selectedPersona)
	}, [personaItems])

	// Called as a function, not rendered as <PersonaList />: it is redefined on every
	// render, so as a component it would remount the tiles' checkboxes on each click.
	const PersonaList = () => {

		const list = personaItems?.filter((persona) => {
			if (searchTerm === "") {
				return personaItems
			}

			return persona?.name.toLowerCase().includes(searchTerm.toLowerCase())

		}).map((persona, index) => (
			<SelectorBox
				key={index}
				label={persona?.name}
				sublabel={(isUseCaseEtrEnabled && persona?.is_etr_issuer) && 'ETR Issuer'}
				description={persona?.description}
				submit={
					() => handleUpdatePersonaSelect(persona)
				}
				disabled={persona.status === statuses.pending}
				active={persona?.$selected}
				handleCheckInfo={() => handleCheckInfo(persona)}
				data-testid={`persona-selector-${index}`}
			/>
		))

		return (
			<Grid columns={{ xs: 1, md: 2, lg: 3 }} gap={2.2}>
				{list.length > 0 ? list : <div>No persona to enroll</div>}
			</Grid>
		)
	}

	/*=================================================
		To Be Added List
	/================================================*/

	const AddedList = ({ data }) => {
		const list = data.filter(item => item.organizationPersonaId === undefined && item.$selected === true).map((persona, index) => {
			return (
				<div className="layout-request__tile__content__sub__header request-enrolment-admin__confirmation-tile__right__sub-header" key={index}>
					<div className="request-enrolment-admin__confirmation-tile__sub-header__icon confirmation-tile__persona-icon">
						<IconPerson />
					</div>
					<div className="request-enrolment-admin__confirmation-tile__sub-header__content">
						{persona.name}
					</div>
				</div>
			)
		})

		return (list.length > 0 ? list : "No new persona to add")

	}

	/*=================================================
		To Be Removed List
	/================================================*/

	const RemovedList = ({ data }) => {
		const list = data.filter(item => item.organizationPersonaId !== undefined && item.$selected === false).map((persona, index) => {
			return (
				<div className="layout-request__tile__content__sub__header request-enrolment-admin__confirmation-tile__right__sub-header" key={index}>
					<div className="request-enrolment-admin__confirmation-tile__sub-header__icon confirmation-tile__persona-icon">
						<IconPerson />
					</div>
					<div className="request-enrolment-admin__confirmation-tile__sub-header__content">
						{persona.name}
					</div>
				</div>
			)
		})

		return (list.length > 0 ? list : "No persona to remove")
	}

	/*=================================================
		To Be Enabled List
	/================================================*/

	const EnabledList = () => {

		let agentPrincipalText = 'No enrolment'
		if (isAgentPrincipal_Enabled !== enableExternalParticipant && !isAgentPrincipal_PendingApproval) {
			agentPrincipalText = 'Agent - Principal Function'
		}

		return (
			<div className="layout-request__tile__content__sub__header request-enrolment-admin__confirmation-tile__right__sub-header" >
				<div className="request-enrolment-admin__confirmation-tile__sub-header__icon confirmation-tile__persona-icon">
					<UserGroup />
				</div>
				<div className="request-enrolment-admin__confirmation-tile__sub-header__content">
					{agentPrincipalText}
				</div>
			</div>
		)
	}

	/*=================================================
		Confirmation List
	/================================================*/

	const ConfirmationList = () => {
		return (
			<Grid
				columns={{ xs: 'minmax(0, 1fr)', md: '400px minmax(0, 1fr)' }}
				columnGap={0}
				rowGap={0}
				alignItems="start"
				sx={{ '& .layout-request__tile__content': { padding: 0 } }}
			>
				<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__left">
					<DataElementCard useCaseItem={useCaseItem} />
				</div>

				<Box display="flex" direction="column" gap={0}>
					<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__right">
						<div className="layout-request__tile__content">
							<div className="request-enrolment-confirmation-header request-enrolment-admin__confirmation-tile__right__header">
								<div className="layout-request__title__content__header__left">
									<div className="request-title request-enrolment-confirmation-header-title">
										You are adding
									</div>
								</div>
								<div className="layout-request__title__content__header__right">
									<Button text="Edit" ghost submit={() => {
										goToStep(1)
									}} />
								</div>
							</div>
							<div className="enrolment-request__tile__content">
								<AddedList data={personaItems} />
							</div>
						</div>
					</div>
					<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__right">
						<div className="layout-request__tile__content">
							<div className="request-enrolment-confirmation-header request-enrolment-admin__confirmation-tile__right__header">
								<div className="layout-request__title__content__header__left">
									<div className="request-title request-enrolment-confirmation-header-title">
										You are removing
									</div>
								</div>
								<div className="layout-request__title__content__header__right">
									<Button text="Edit" ghost submit={() => {
										goToStep(1)
									}} />
								</div>
							</div>
							<div className="enrolment-request__tile__content">
								<RemovedList data={personaItems} />
							</div>
						</div>
					</div>
					{isAgentEnabled && (
						<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__right">
							<div className="layout-request__tile__content">
								<div className="request-enrolment-confirmation-header request-enrolment-admin__confirmation-tile__right__header">
									<div className="layout-request__title__content__header__left">
										<div className="request-title request-enrolment-confirmation-header-title">
											{isAgentPrincipal_PendingApproval ? "You are enabling" : (isAgentPrincipal_Enabled && !enableExternalParticipant) ? "You are disabling" : "You are enabling"}
										</div>
									</div>
									<div className="layout-request__title__content__header__right">
										<Button text="Edit" ghost submit={() => {
											goToStep(1)
										}} />
									</div>
								</div>
								<div className="enrolment-request__tile__content">
									<EnabledList />
								</div>
							</div>
						</div>
					)}
					{isUseCaseEtrEnabled && (
						<ConfigureIssuanceCard
							blockchainNetworks={blockchainNetworks}
							noEtrPersonaSelected={!IsEtrPersonaArray}
							etrIssuanceEnabled={etrIssuanceEnabled}
							handleEdit={() => {
								goToStep(2)
							}}
						/>
					)}
				</Box>
			</Grid>
		)
	}


	/*=================================================
		Modal
	/================================================*/

	const { visible: visibleConfirmation, show: showConfirmation, hide: hideConfirmation } = useModal()
	const { visible: visibleDisableConfirmation, show: showDisableConfirmation, hide: hideDisableConfirmation } = useModal()
	const { visible: visiblePersonaInfo, show: showPersonaInfo, hide: hidePersonaInfo } = useModal()

	const [modalBodyText, setModalBodyText] = useState([])
	const [modalHeaderText, setModalHeaderText] = useState()

	/*=================================================
		Step
	/================================================*/

	const  firstStepList =[
		{
			stepTitle: 'Edit personas',
		},
		{
			stepTitle: 'Issuance Configuration',
		},
		{
			stepTitle: 'Review & Confirm',
		},
	]
	const secondStepList= [
		{
			stepTitle: 'Edit personas',
		},
		{
			stepTitle: 'Review & Confirm',
		}
	]

	const steps = useMemo(() => {
		if (isUseCaseEtrEnabled) {
			// return ['Edit personas', 'Issuance Configuration', 'Review & Confirm']
			return firstStepList;
		}
		// return ['Edit personas', 'Review & Confirm']
		return secondStepList;
	}, [isUseCaseEtrEnabled])

	const { step, goToStep, prevStep, nextStep } = useStepList(steps)

	/*===============================================================
		ETR Issuance Disable Modal
	/================================================================*/

	const { visible: visibleEditPersonaDisbleEtrIssuanceModal, show: showEditPersonaDisbleEtrIssuanceModal, hide: hideEditPersonaDisbleEtrIssuanceModal } = useModal()
	const { visible: visibleDisbleEtrIssuanceModal, show: showDisbleEtrIssuanceModal, hide: hideDisbleEtrIssuanceModal } = useModal()
	const modalNextStep = () => { nextStep() }

	/*===============================================================
		ETR Issuance Exclude All Modal
	/================================================================*/

	const { visible: visibleExcludeAllEtrIssuanceModal, show: showExcludeAllEtrIssuanceModal, hide: hideExcludeAllEtrIssuanceModal } = useModal()

	async function toAddPersona() {
		if (loading) {
			return
		}

		setLoading(true)

		const addedPayload = personaItems.filter(item => item.organizationPersonaId === undefined && item.$selected === true).map(persona => {
			return {
				personaId: persona.id,
				orgId: orgId,
				useCaseId: useCaseId
			}
		})

		const [error, response] = await api.create(addedPayload)

		setLoading(false)

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}
	}

	async function toRemovePersona() {
		if (loading) {
			return
		}

		setLoading(true)

		const removeIds = personaItems.filter(item => item.organizationPersonaId !== undefined && item.$selected === false).map(persona => {
			return (
				persona.organizationPersonaId
			)
		})

		const removePayload = {
			useCaseId: useCaseId,
			ids: removeIds
		}

		const [error, response] = await api.deletePersona(removePayload)

		setLoading(false)

		if (error) {
			addToastMessage(error?.message, toastType.error)
		}

		const checkAdd = personaItems.filter(item => item.organizationPersonaId === undefined && item.$selected === true)

		if (checkAdd.length > 0) {
			return toAddPersona()
		}
	};

	async function toUpdateAgentPrincipalEnrolment() {
		if (loading) {
			return
		}

		setLoading(true)

		const enablePayload = [{
			useCaseId: useCaseId,
			orgId: orgId,
			isAgent: enableExternalParticipant
		}]

		const [error, response] = await api.enablePersona(enablePayload)

		setLoading(false)

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}
	}

	async function toUpdateETRIssuance() {
		if (loading) {
			return
		}

		setLoading(true)

		const payload = [{
			useCaseId: useCaseId,
			orgId: orgId,
			etrIssuance: {
				isEnabled: (etrIssuanceEnabled && IsEtrPersonaArray),
				networkIDs: selectedNetworkIds
			}
		}]

		const [error] = await api.enablePersona(payload)

		setLoading(false)

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}
	}

	function handleSuccessfulRedirect(messageType) {
		if (role === 'admin') {
			goTo(`/admin/use-cases/${orgId}/personas/success`, { from: "editPersona", data: participantData })
		}

		if (role === 'participant') {
			goTo(`/use-cases/${useCaseId}/personas/success`, { from: "editPersona", messageType: messageType })
		}
	}

	function handleConfirmationMessage(modalType) {
		const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2);
		switch (modalType) {
			case "agentPrincipal":
				setModalHeaderText("Disable Agent - Principal Function")
				setModalBodyText([`By disabling Agent Enrolment, you will no longer be able to ${isAgentPrincipalPhase2Enabled ? "provide and" : ""} consume Data Element on behalf of all your principal(s). Are you sure you want to proceed? `])
				break;
			case "agentPrincipal-persona":
				setModalHeaderText("Confirm Removal of Persona(s)")
				if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === false) {
					setModalBodyText([
						`By disabling Agent - Principal function, you will no longer be able to ${isAgentPrincipalPhase2Enabled ? "provide and" : ""} consume Data Element on behalf of all your principal(s).`,
						`By submitting the changes, you will remove the following Persona(s) from the Use Case "${useCase?.name}" and access for the associated Data Elements & Participating Organization(s).`
					])
				} else {
					setModalBodyText([`By submitting the changes, you will remove the following Persona(s) from the Use Case "${useCase?.name}" and access for the associated Data Elements & Participating Organization(s).`])
				}
				break;
			default:
				throw new Error("Invalid Modal Type")
		}
	}

	async function confirm() {
		if (removePersonas.length > 0) {
			/** Scenario: Remove personas and potentially disable agent */
			handleConfirmationMessage("agentPrincipal-persona");
			showConfirmation();
		} else if (addPersonas.length > 0) {
			if (isAgentEnabled) {
				if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === false) {
					/** Scenario: Add personas and Subsequently Disable Agent */
					handleConfirmationMessage("agentPrincipal");
					showDisableConfirmation();
				} else if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === true) {
					/** Scenario: Add personas and Subsequently Enable Agent */
					toAddPersona();
					toUpdateAgentPrincipalEnrolment();
					return handleSuccessfulRedirect('enableAgentPrincipal-persona');
				} else {
					/** Scenario: Add personas */
					toAddPersona();
					return handleSuccessfulRedirect('addPersonas');
				}
			} else if (isUseCaseEtrEnabled) {
				/** Scenario: Add personas and update ETR Issuance */
				if (hasETRIssuanceChanged()) {
					toUpdateETRIssuance();
				}
				toAddPersona();
				return handleSuccessfulRedirect('addPersonas');
			} else {
				/** Scenario: Add personas */
				toAddPersona();
				return handleSuccessfulRedirect('addPersonas');
			}
		} else if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === false) {
			/** Scenario: Disable agent */
			handleConfirmationMessage("agentPrincipal");
			showDisableConfirmation();
		} else {
			/** Scenario: Enable agent */
			toUpdateAgentPrincipalEnrolment();
			return handleSuccessfulRedirect('enableAgentPrincipal');
		}
	}

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		goTo(-1)
	}

	/*=================================================
		Calculated Values
	/================================================*/
	const isAgentPrincipal_Enabled = isAgentPrincipalEnabled(useCaseItem?.[0]?.agentStatus ?? useCase.agentStatus);
	const isAgentPrincipal_PendingApproval = isAgentPrincipalPendingApproval(useCaseItem?.[0]?.agentStatus ?? useCase.agentStatus);
	const isAgentEnabled = useCase?.isAgentEnabled ?? useCaseItem?.[0]?.isAgentEnabled

	const noPersonaMessage = "Agent Principal Function can only be turned on when at least one persona is selected";
	const pendingApprovalMessage = "Agent Principal Enrolment Pending Approval by Admin";

	function hasETRIssuanceChanged() {
		return isOrgEtrEnabled !== (IsEtrPersonaArray && etrIssuanceEnabled) || !isEqual(existingNetworkIds.sort(), selectedNetworkIds.sort());
	}

	function handleModalConfirmation() {
		if (removePersonas.length > 0 || addPersonas.length > 0) {
			toRemovePersona()
		}
		if ((enableExternalParticipant !== isAgentPrincipal_Enabled)) {
			toUpdateAgentPrincipalEnrolment()
		}
		if (isUseCaseEtrEnabled && hasETRIssuanceChanged()) {
			toUpdateETRIssuance()
		}

		if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === false && (removePersonas.length > 0 || addPersonas.length > 0)) {
			/** Scenario: Add and Remove personas and disable agent */
			handleSuccessfulRedirect('disableAgentPrincipal-persona')
		} else if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === true && removePersonas.length > 0) {
			/** Scenario: Remove personas and Enable agent */
			handleSuccessfulRedirect('enableAgentPrincipal-persona');
		} else if (enableExternalParticipant !== isAgentPrincipal_Enabled && enableExternalParticipant === false) {
			/** Scenario: Disable agent */
			handleSuccessfulRedirect('disableAgentPrincipal')
		} else {
			/** Scenario: Add and Remove personas*/
			handleSuccessfulRedirect('removePersonas')
		}
	}

	return (
		<>
			<div className="title-header edit-persona-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
				<Box display="flex" justify="end"></Box>
			</div>

			<Stepper  steps={steps} currentStep={step-1}/>

			<div style={{ marginTop: rem(10) }}></div>

			{step === 1 && (
				<>
					<div className="layout-enroll">
						<div className="layout-enroll__content">
							<div className="view-info--details">
								<div className="view-info--selector-box-wrap">
									<Box display="flex" wrap="wrap" justify="between">
										<div className="layout-default__desc__data__element">
											Choose personas that best describe your company's profile
										</div>

										{isAgentEnabled && role === "participant" && (
											<div className="layout-enrolment__toggle__content" data-tooltip-id="tooltip" data-tooltip-content={noOfPersona === 0 && !isAgentPrincipal_PendingApproval ? noPersonaMessage : isAgentPrincipal_PendingApproval ? pendingApprovalMessage : null}
												data-tooltip-hidden={!(isAgentPrincipal_PendingApproval || noOfPersona === 0)}
											>
												<div className="layout-enrolment__info" data-testid="agent-principal-info" onClick={() => { showParticipantInfoModal(); }}>
													<IconEye />
												</div>
												<div> Enable Agent - Principal Function :</div>
												<ToggleSwitch3 id="enableExternalParticipant" checked={enableExternalParticipant} onChange={handledEnableExternalParticipant} disabled={isAgentPrincipal_PendingApproval || noOfPersona === 0}
													data-testid="enable-external-participant-toggle"
												/>
												<div> {enableExternalParticipant ? "Yes" : "No"}
												</div>
											</div>
										)}
									</Box>

									<Box display="flex" align="center" mb={4}>
										{
											feature(featureConstant.NEW_DESIGN_SYSTEM) ? (
												<div style={{ maxWidth: "310px", width: '310px' }}>
													<Search placeholder="Search" onChange={(e) => { setSearchTerm(e.target.value) }} value={searchTerm} onCancel={() => setSearchTerm("")} />
												</div>
											) : (

												<InputSuffixIcon data-testid="search-box-input" maxWidth="310" placeholder="Search" icon={<IconSearch />} onChange={(e) => { setSearchTerm(e.target.value) }} />
											)
										}

										<div className="request-enrolment__search-box__selection">
											{noOfPersona} Selected
										</div>
										<Button data-testid="reset-selection-button" text="Reset Selection" noborder addClass={"request-enrolment__search-box__clear-selection"} submit={() => clearPersonaSelection()} />
									</Box>

									{PersonaList()}
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			{step === 2 && isUseCaseEtrEnabled && (
				<ConfigureIssuanceSection
					networkId={useCase?.etrIssuance?.networkIDs}
					setEtrIssuanceEnabled={setEtrIssuanceEnabled}
					status={useCase?.status}
					noEtrPersonaSelected={!IsEtrPersonaArray}
					etrIssuanceEnabled={etrIssuanceEnabled}
					toggleEtrIssuance={toggleEtrIssuance}
					blockchainNetworks={blockchainNetworks}
					loading={loadingBlockchainNetworks}
					error={errorBlockchainNetworks}
					toggleBlockchainNetwork={toggleBlockchainNetwork}
					toggleAllBlockchainNetworks={toggleAllBlockchainNetworks}
				/>
			)}

			{((step === 2 && !isUseCaseEtrEnabled) || (step === 3 && isUseCaseEtrEnabled)) && (
				<>
					<div className="layout-enroll">
						<div className="layout-enroll__content">
							<ConfirmationList />
						</div>
					</div>
				</>
			)}
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button text="Cancel" noborder submit={() => onBack()} />
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						<div className="layout-default--margin-right">
							{step > 1 && (<Button data-testid="previous-button" text="Previous" ghost submit={() => prevStep()} />)}
						</div>
						{step === 1 && (
							<Button
								data-testid="next-button"
								text="Next"
								disabled={!isModified && enableExternalParticipant === isAgentPrincipal_Enabled}
								submit={() => {
									const removePersonas = personaItems.filter(item => item.organizationPersonaId !== undefined && item.$selected === false);
									const addPersonas = personaItems.filter(item => item.organizationPersonaId === undefined && item.$selected === true)
									setRemovePersonas(removePersonas);
									setAddPersonas(addPersonas);
									if (isUseCaseEtrEnabled && !IsEtrPersonaArray && removePersonas.some(s => s.is_etr_issuer)) {
										showEditPersonaDisbleEtrIssuanceModal();
									} else {
										nextStep()
									}
								}}
							/>)}
						{step === 2 && isUseCaseEtrEnabled && (
							<Button
								data-testid="next-button"
								text="Next"
								submit={() => {
									if (IsEtrPersonaArray && etrIssuanceEnabled && !isAnyBlockchainSelected) {
										showExcludeAllEtrIssuanceModal();
									} else if (!etrIssuanceEnabled && IsEtrPersonaArray) {
										showDisbleEtrIssuanceModal();
									} else {
										nextStep();
									}
								}} />
						)}
						{((step === 2 && !isUseCaseEtrEnabled) || (step === 3 && isUseCaseEtrEnabled)) && (
							<Button
								data-testid="save-button" text="Save" id="edit-personas-submit-button" submit={() => confirm()}
								full disabled={loading}
								loading={loading}
							/>
						)}
					</div>
				</div>
			</div>

			<DisableEtrIssuance
				data-testid="edit-persona-disable-etr-issuance"
				visible={visibleEditPersonaDisbleEtrIssuanceModal}
				hide={hideEditPersonaDisbleEtrIssuanceModal}
				show={showEditPersonaDisbleEtrIssuanceModal}
				handleSubmit={modalNextStep}
				body={<div className="etr-issuance-modal__content center">
					<p>Your new persona selection will disable ETR Issuance.</p>
					<p>Proceed anyway?</p>
				</div>}
			/>

			<DisableEtrIssuance
				data-testid="etr-issuance"
				visible={visibleDisbleEtrIssuanceModal}
				hide={hideDisbleEtrIssuanceModal}
				show={showDisbleEtrIssuanceModal}
				handleSubmit={modalNextStep}
			/>

			<ExcludeAllEtrIssuance
				visible={visibleExcludeAllEtrIssuanceModal}
				hide={hideExcludeAllEtrIssuanceModal}
				show={showExcludeAllEtrIssuanceModal}
				handleSubmit={modalNextStep}
			/>

			<AgentPersonaModal
				data-testid="remove-agent-and-persona-modal"
				visible={visibleConfirmation}
				hide={hideConfirmation}
				handleSubmission={handleModalConfirmation}
				dataItems={personaItems}
				header={modalHeaderText}
				body={modalBodyText}
			/>

			<AgentPersonaModal
				data-testid="disable-agent-modal"
				visible={visibleDisableConfirmation}
				hide={hideDisableConfirmation}
				handleSubmission={handleModalConfirmation}
				header={modalHeaderText}
				body={modalBodyText}
			/>

			<PersonaInfoModal
				visible={visiblePersonaInfo}
				hide={hidePersonaInfo}
				persona={persona}
			/>

			<AgentPrincipalInformationModal
				visible={visibleParticipantInfoModal}
				hide={hideParticipantInfoModal}
				show={showParticipantInfoModal}
			/>

			<AgentPrincipalTnCModal
				visible={visibleParticipantsTncModal}
				hide={hideParticipantsTncModal}
				show={showParticipantsTncModal}
				acceptParticipantTnc={acceptParticipantTnc}
			/>

			<Toast toastList={toastList} />
			<Tooltip />
		</>
	);
}
export default Page
